import _defineProperty from "/vercel/path0/node_modules/.pnpm/@babel+runtime@7.28.4/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/vercel/path0/node_modules/.pnpm/@babel+runtime@7.28.4/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import { browserPerformanceTimeOrigin, getActiveSpan, getRootSpan, spanToJSON, getCurrentScope, timestampInSeconds, startSpan, SEMANTIC_ATTRIBUTE_SENTRY_SOURCE, SEMANTIC_ATTRIBUTE_SENTRY_OP, SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN } from '@sentry/core';
import { addPerformanceInstrumentationHandler } from './instrument.js';
import { getBrowserPerformanceAPI, msToSec } from './utils.js';

// ElementTiming interface based on the W3C spec

/**
 * Start tracking ElementTiming performance entries.
 */
function startTrackingElementTiming() {
  var performance = getBrowserPerformanceAPI();
  if (performance && browserPerformanceTimeOrigin()) {
    return addPerformanceInstrumentationHandler('element', _onElementTiming);
  }
  return function () {
    return undefined;
  };
}

/**
 * exported only for testing
 */
var _onElementTiming = function _onElementTiming(_ref) {
  var entries = _ref.entries;
  var activeSpan = getActiveSpan();
  var rootSpan = activeSpan ? getRootSpan(activeSpan) : undefined;
  var transactionName = rootSpan ? spanToJSON(rootSpan).description : getCurrentScope().getScopeData().transactionName;
  entries.forEach(function (entry) {
    var _elementEntry$element, _attributes;
    var elementEntry = entry;

    // Skip entries without identifier (elementtiming attribute)
    if (!elementEntry.identifier) {
      return;
    }

    // `name` contains the type of the element paint. Can be `'image-paint'` or `'text-paint'`.
    // https://developer.mozilla.org/en-US/docs/Web/API/PerformanceElementTiming#instance_properties
    var paintType = elementEntry.name;
    var renderTime = elementEntry.renderTime;
    var loadTime = elementEntry.loadTime;

    // starting the span at:
    // - `loadTime` if available (should be available for all "image-paint" entries, 0 otherwise)
    // - `renderTime` if available (available for all entries, except 3rd party images, but these should be covered by `loadTime`, 0 otherwise)
    // - `timestampInSeconds()` as a safeguard
    // see https://developer.mozilla.org/en-US/docs/Web/API/PerformanceElementTiming/renderTime#cross-origin_image_render_time
    var _ref2 = loadTime ? [msToSec(loadTime), 'load-time'] : renderTime ? [msToSec(renderTime), 'render-time'] : [timestampInSeconds(), 'entry-emission'],
      _ref3 = _slicedToArray(_ref2, 2),
      spanStartTime = _ref3[0],
      spanStartTimeSource = _ref3[1];
    var duration = paintType === 'image-paint' ?
    // for image paints, we can acually get a duration because image-paint entries also have a `loadTime`
    // and `renderTime`. `loadTime` is the time when the image finished loading and `renderTime` is the
    // time when the image finished rendering.
    msToSec(Math.max(0, (renderTime !== null && renderTime !== void 0 ? renderTime : 0) - (loadTime !== null && loadTime !== void 0 ? loadTime : 0))) :
    // for `'text-paint'` entries, we can't get a duration because the `loadTime` is always zero.
    0;
    var attributes = (_attributes = {}, _defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_attributes, SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN, 'auto.ui.browser.elementtiming'), SEMANTIC_ATTRIBUTE_SENTRY_OP, 'ui.elementtiming'), SEMANTIC_ATTRIBUTE_SENTRY_SOURCE, 'component'), 'sentry.span_start_time_source', spanStartTimeSource), 'sentry.transaction_name', transactionName), 'element.id', elementEntry.id), 'element.type', ((_elementEntry$element = elementEntry.element) === null || _elementEntry$element === void 0 || (_elementEntry$element = _elementEntry$element.tagName) === null || _elementEntry$element === void 0 ? void 0 : _elementEntry$element.toLowerCase()) || 'unknown'), 'element.size', elementEntry.naturalWidth && elementEntry.naturalHeight ? "".concat(elementEntry.naturalWidth, "x").concat(elementEntry.naturalHeight) : undefined), 'element.render_time', renderTime), 'element.load_time', loadTime), _defineProperty(_defineProperty(_defineProperty(_attributes, 'element.url', elementEntry.url || undefined), 'element.identifier', elementEntry.identifier), 'element.paint_type', paintType));
    startSpan({
      name: "element[".concat(elementEntry.identifier, "]"),
      attributes: attributes,
      startTime: spanStartTime,
      onlyIfParent: true
    }, function (span) {
      span.end(spanStartTime + duration);
    });
  });
};
export { _onElementTiming, startTrackingElementTiming };